import { z } from "zod";
import {
  Button,
  Grid,
  Header,
  Icon,
  Message,
  Modal,
  Segment,
} from "semantic-ui-react";
import { AxiosEmployeeAPI, AxiosEmployeeManagerAPI, ManagerChangeStatus } from "../Api";
import { Form, FormSelect } from "../../common/components/Form";
import { Formik, useField } from "formik";
import React, { useState } from "react";

import { ChangeButton } from "../../common/components/ChangeButton";
import { ChangeDateInput } from "../../common/components/ChangeDateInput";
import { HeaderItem } from "../../common/components/HeaderItem";
import { Icons } from "../../common/Icons";
import ReactFocusLock from "react-focus-lock";
import { ReviewAccessInput } from "../../common/components/ReviewAccessInput";
import { ServerErrorMessage } from "../../common/components/ServerErrorMessage";
import { ToastId } from "../../common/ToastIds";
import { toast } from "react-toastify";
import { useAsync } from "react-async";
import { useDebouncedSearch } from "../../utils/Hooks";
import { useSettings } from "../../utils/Settings";

const ManagerInfoPanel = ({ header = "Gestor", manager }) => {
  return (
    <>
      <Header>{header}</Header>
      <HeaderItem icon={Icons.NAME} label="Nome" value={manager?.name} />
      <HeaderItem
        icon={Icons.EMAIL}
        label="E-mail"
        value={manager?.email || manager?.employee_code}
      />
      <HeaderItem
        label="Cargo"
        icon={Icons.POSITION}
        value={manager?.current_position?.position.name}
      />
      <HeaderItem
        label="Área"
        icon={Icons.AREA}
        value={manager?.current_area?.area.name}
      />
    </>
  );
};

const PreviewChangePanel = ({ currentManager, selectedManager }) => {
  return (
    <Grid stackable>
      <Grid.Column width={7}>
        <ManagerInfoPanel header="Gestor atual" manager={currentManager} />
      </Grid.Column>
      <Grid.Column width={2} verticalAlign={"middle"}>
        <Icon size="big" name={"arrow right"} />
      </Grid.Column>
      <Grid.Column width={7}>
        <ManagerInfoPanel header="Novo gestor" manager={selectedManager} />
      </Grid.Column>
    </Grid>
  );
};

const EmployeeManagerSelect = ({
  onManagerChange,
  currentEmployeeCode,
  ...props
}) => {
  const [, , helpers] = useField(props);

  const [, debouncedSearch, setSearch] = useDebouncedSearch();

  const {
    data: employees,
    isPending,
    error,
  } = useAsync({
    promiseFn: AxiosEmployeeAPI.getAllAccessible,
    search: debouncedSearch,
    watch: debouncedSearch,
  });

  const handleChange = (e, v) => {
    helpers.setValue(v.value);

    if (onManagerChange) {
      const selectedOption = options.find((e) => e.value === v.value);
      const manager = selectedOption ? selectedOption.employee : null;
      onManagerChange(manager);
    }
  };

  const handleSearch = (e, { searchQuery }) => {
    setSearch(searchQuery);
  };

  let options = employees
    ? employees.results
        .filter((e) => e.employee_code !== currentEmployeeCode)
        .map((e) => ({
          text: e.name,
          employee: e,
          value: e.api_url,
          content: (
            <Header style={{ fontSize: "1rem" }}>
              {e.name}
              <Header.Subheader>
                {e?.email || e.employee_code} /{" "}
                {e.current_area ? e.current_area.area.name : "(Sem área)"} /{" "}
                {e.current_position
                  ? e.current_position.position.name
                  : "(Sem cargo)"}
              </Header.Subheader>
            </Header>
          ),
        }))
    : [];

  return (
    <Grid.Row>
      <Grid.Column>
        {error && (
          <Message error visible>
            <Message.Content>
              Não foi possível carregar a lista de funcionários, por favor tente
              novamente.
            </Message.Content>
          </Message>
        )}
        <FormSelect
          clearable
          scrolling
          loading={isPending}
          onSearchChange={handleSearch}
          onChange={handleChange}
          options={options}
          {...props}
        />
      </Grid.Column>
    </Grid.Row>
  );
};

const ManagerChangeSchema = z.object({
  //TODO: Add appropriate employee type
  employee: z
    .object({})
    .passthrough()
    .refine((val) => val !== null && val !== undefined, {
      message: "Employee is required",
    }),
  new_manager: z
    .string({
      required_error: "Por favor insira o gestor",
    })
    .min(1, "Por favor insira o gestor"),
  change_date: z.coerce.date({
    required_error: "Por favor insira a data",
    invalid_type_error: "Data inválida",
  }),
});

const EmployeeManagerChangeForm = ({
  employee,
  currentManager,
  onSuccess,
  onCancel,
}) => {
  const [selectedManager, setSelectedManager] = useState();

  const { run, isPending, isFulfilled, error, data } = useAsync({
    deferFn: AxiosEmployeeManagerAPI.changeManager,
  });

  const { data: dataSettings, isFetching: isAccessPending } = useSettings();

  if (isAccessPending) {
    return null;
  }

  const reviewAccessActive = dataSettings
    ? dataSettings.find((setting) => setting.key === "REVIEW_ACCESS_ACTIVE")
        .value === "True"
    : false;

  const formData = {
    employee: employee,
    new_manager: "",
    change_date: "",
    review_access: reviewAccessActive,
  };

  const handleSubmit = (values, { setSubmitting }) => {
    run(values);
    setSubmitting(false);
  };

  if (isFulfilled) onSuccess(data);

  return (
    <>
      <Formik
        initialValues={formData}
        onSubmit={handleSubmit}
        validate={(values) => {
          const result = ManagerChangeSchema.safeParse(values);
          if (!result.success) {
            return result.error.flatten().fieldErrors;
          }
        }}
      >
        <Form loading={isPending}>
          <Segment basic>
            <ServerErrorMessage error={error} />
            <Grid>
              <EmployeeManagerSelect
                name="new_manager"
                label="Novo gestor"
                currentEmployeeCode={employee.employee_code}
                onManagerChange={setSelectedManager}
              />
              <ChangeDateInput />
              {reviewAccessActive && <ReviewAccessInput />}
            </Grid>
          </Segment>
          <Segment basic>
            <PreviewChangePanel
              currentManager={currentManager}
              selectedManager={selectedManager}
            />
          </Segment>
          <Segment basic>
            <Button type="submit" primary>
              Alterar
            </Button>
            <Button basic onClick={onCancel}>
              Cancelar
            </Button>
          </Segment>
        </Form>
      </Formik>
    </>
  );
};

export const EmployeeManagerChangeModal = ({
  employee,
  currentManager,
  reload,
  trigger,
}) => {
  const [open, setOpen] = useState(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const handleSuccess = (data) => {
    handleClose();
    reload();
    if (data.status === ManagerChangeStatus.ACCEPTED) {
      toast.success("Gestor alterado com sucesso!", ToastId.MANAGER_CHANGE);
    } else if (data.status === ManagerChangeStatus.PENDING) {
      toast.success(
        "Requisição de troca de gestor realizada com sucesso!",
        ToastId.MANAGER_REQUEST,
      );
    }
  };

  return (
    <Modal
      open={open}
      onOpen={handleOpen}
      onClose={handleClose}
      onFocus={(e) => e.stopPropagation()}
      trigger={
        trigger || <ChangeButton disabled={!employee._actions.change_manager} />
      }
      style={{ maxWidth: "700px" }}
    >
      <Modal.Header>Alterar gestor de {employee.name}</Modal.Header>
      <Modal.Content>
        <ReactFocusLock>
          <EmployeeManagerChangeForm
            employee={employee}
            currentManager={currentManager}
            onSuccess={handleSuccess}
            onCancel={handleClose}
          />
        </ReactFocusLock>
      </Modal.Content>
    </Modal>
  );
};
